/* eslint-disable react/no-array-index-key */
import { CircularProgress, makeStyles, Paper, Theme, Typography } from '@material-ui/core';
import { BLANK_IMAGE, InfoCompPanelHeader, useModePanelStyle } from '@moderepo/mode-app-components';
import clsx from 'clsx';
import { CSSProperties, Fragment, useEffect, useMemo, useState } from 'react';
import { AlertSeverity, AlertState } from '@moderepo/mode-internal-apis';
import { AppAPI } from '@moderepo/mode-apis';
import { EntityUIComponentCollection, useSelectTranslatedEntityClasses, EntityUIComponentProps, CardHighlightStyle, isCardHighlightStyle } from '..';
import { useSelectEntity, useSelectProject } from '../../../utils/dataStateHooks';
import { RitzComponentsCollection } from '../../../utils/ritzComponentsCollection';
import { isObjectLiteral, isValidSettingBooleanValue, isValidSettingNumberValue, isValidSettingStringValue } from '../../../utils/utils';
import { EntityDataSettings, isCustomAttributeEntityDataSettings, isSubTypeEntityDataSettings, isTSDBEntityDataSettings,
    useGetEntitiesData } from '.';
import { useRitzTranslation } from '../../../utils/customHooks';
import { SettingsErrorLogger } from '../../../utils/logger';
import { NO_VALUE } from '../../../utils/constants';



const useStyle = makeStyles((theme: Theme) => {
    return {
        root: {
            '&.clickable': {
                cursor: 'pointer',
            },

            '&.highlight': {
                '&.bar': {
                    borderBottom     : '6px solid transparent',
                    borderBottomColor: theme.palette.success.light,

                    [`&.severity-${AlertSeverity.HIGH}`]: {
                        borderBottomColor: theme.palette.error.light,
                    },

                    [`&.severity-${AlertSeverity.MODERATE}`]: {
                        borderBottomColor: theme.palette.warning.light,
                    },

                    [`&.severity-${AlertSeverity.LOW}`]: {
                        borderBottomColor: theme.palette.info.light,
                    },
                },
                '&.border': {
                    border                              : '6px solid transparent',
                    borderColor                         : theme.palette.success.light,
                    [`&.severity-${AlertSeverity.HIGH}`]: {
                        borderColor: theme.palette.error.light,
                    },

                    [`&.severity-${AlertSeverity.MODERATE}`]: {
                        borderColor: theme.palette.warning.light,
                    },

                    [`&.severity-${AlertSeverity.LOW}`]: {
                        borderColor: theme.palette.info.light,
                    },
                },
                '&.background': {
                    '& $content': {
                        background: theme.palette.success.light,
                        color     : theme.palette.success.contrastText,
                    },
                    [`&.severity-${AlertSeverity.HIGH}`]: {
                        '& $content': {
                            background: theme.palette.error.light,
                            color     : theme.palette.error.contrastText,
                        },
                    },

                    [`&.severity-${AlertSeverity.MODERATE}`]: {
                        '& $content': {
                            background: theme.palette.warning.light,
                            color     : theme.palette.warning.contrastText,
                        },
                    },

                    [`&.severity-${AlertSeverity.LOW}`]: {
                        '& $content': {
                            background: theme.palette.info.light,
                            color     : theme.palette.info.contrastText,
                        },
                    },
                },
            },
        },

        panelContent: {
            height : '100%',
            padding: 0,
        },

        content: {
            width         : '100%',
            height        : '100%',
            overflow      : 'auto',
            display       : 'flex',
            flexDirection : 'column',
            justifyContent: 'center',
        },

        entityName: {
            padding                     : theme.spacing(1, 1, 0, 1),
            [theme.breakpoints.up('lg')]: {
                padding: theme.spacing(2, 2, 0, 2),
            },
        },

        body: {
            width                       : '100%',
            height                      : '100%',
            overflow                    : 'auto',
            padding                     : theme.spacing(1),
            display                     : 'flex',
            flexDirection               : 'row',
            justifyContent              : 'center',
            [theme.breakpoints.up('lg')]: {
                padding: theme.spacing(2),
            },
        },

        imageContainer: {
            flex                        : 1,
            marginRight                 : theme.spacing(1),
            [theme.breakpoints.up('lg')]: {
                marginRight: theme.spacing(2),
            },
        },

        image: {
            width     : '100%',
            height    : '100%',
            display   : 'inline-block',
            objectFit : 'cover',
            background: theme.palette.background.default,
        },

        dataDisplayContainer: {
            flex          : 2,
            width         : '100%',
            height        : 'fit-content',
            overflow      : 'auto',
            display       : 'flex',
            flexDirection : 'column',
            justifyContent: 'center',
            alignSelf     : 'center',
        },

        entityData: {
            flex          : 1,
            display       : 'flex',
            flexDirection : 'row',
            alignItems    : 'center',
            justifyContent: 'space-between',
            margin        : theme.spacing(0.5, 0),
        },

        entityDataLabel: {
            lineHeight                  : '1em',
            paddingRight                : theme.spacing(1),
            [theme.breakpoints.up('lg')]: {
                paddingRight: theme.spacing(2),
            },
        },

        entityDataValueAndUnit: {
        },
    };
}, { name: 'EntitySummaryWithStatusUIComponent', index: 1 });



/**
 * The interface for storing 1 entity's alerts statistic. The key will be the severity # and the value is the alert count for that severity
 * For example:
 *      {
 *          1: 23,      // 23 HIGH alerts
 *          2: 3        // 3 MODERATE alerts
 *          3: 5        // 5 LOW alerts
 *      }
 */
interface AlertsSummary {
    [AlertSeverity.HIGH]: number;
    [AlertSeverity.MODERATE]: number;
    [AlertSeverity.LOW]: number;
}


/**
 * Given a projectId, entityId, and severity level, fetch ACTIVE alerts for that entity that has the specified severity and return the
 * total number of alerts
 */
const getActiveAlertsCountForOneSeverity = async (
    projectId: number,
    entityId: string,
    severity: AlertSeverity,
): Promise<number> => {
    try {
        const alerts = await AppAPI.getInstance().getAlerts(projectId, {
            entityId,
            state: AlertState.ALERTING,
            severity,
            limit: 1,                   // Only need to fetch 1 alert. We can get the alerts count from the response header.
        });

        return alerts.range?.total ?? 0;
    } catch (error) {
        return 0;
    }
};


/**
 * Get the entity's alert summary
 */
const getEntityAlertsSummary = async (projectId: number, entityId: string): Promise<AlertsSummary> => {
    const alertsSummary: AlertsSummary = {
        [AlertSeverity.HIGH]    : 0,
        [AlertSeverity.MODERATE]: 0,
        [AlertSeverity.LOW]     : 0,
    };
    const supportedSeverities = [AlertSeverity.HIGH, AlertSeverity.MODERATE, AlertSeverity.LOW];
    for (let i = 0; i < supportedSeverities.length; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        alertsSummary[supportedSeverities[i]] = await getActiveAlertsCountForOneSeverity(projectId, entityId, supportedSeverities[i]);
    }

    return alertsSummary;
};



/**
 * This is the interface for the EntitySummaryWithStatusUIComponent's CustomSettings.
 */
export interface EntitySummaryWithStatusUIComponentCustomSettings {
    // Custom title and icon
    readonly titleKey?: string | undefined;
    readonly icon?: string | undefined;

    // To specify how many decimal places to round the value to. This is global setting that apply to all data EXCEPT for data that have
    // their own custom decimalPlaces setting.
    readonly decimalPlaces?: number | undefined;

    // The default value to display when an data does not have a value. This is a global setting that apply to all data EXCEPT for data that
    // have their own defaultValueKey setting.
    readonly defaultValueKey?: string | undefined;

    // Whether or not to include the entity's name in the card
    readonly showEntityName?: boolean | undefined;

    // Whether or not to include the entity's image in the card
    readonly showEntityImage?: boolean | undefined;

    // An array of data to be displayed. The data will be displayed in the same order as they appear in this array.
    readonly displayData: readonly EntityDataSettings[];

    /**
     * To highlight the card based on the most severe alert. For example, if the entity's most sever alert is a HIGH severity, then highlight the
     * card red. If the entity's most severe alert is MODERATE then highlight it YELLOW and so on.
     * The card can be highlighted in different style, bar, border, or background. If this is NOT provided then the component will not highlight
     * the component
     */
    readonly highlightCardStyle?: CardHighlightStyle | undefined;

    readonly styles?: {
        readonly container?: CSSProperties | undefined;
        readonly content?: CSSProperties | undefined;
        readonly name?: CSSProperties | undefined;
        readonly imageContainer?: CSSProperties | undefined;
        readonly image?: CSSProperties | undefined;
        readonly dataContainer?: CSSProperties | undefined;
        readonly dataRow?: CSSProperties | undefined;
        readonly dataLabel?: CSSProperties | undefined;
        readonly dataValue?: CSSProperties | undefined;
    } | undefined;
}


/**
 * Type guard function to validate the obj to make sure it is a type of EntitySummaryWithStatusUIComponentCustomSettings
 */
export const isEntitySummaryWithStatusUIComponentCustomSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is EntitySummaryWithStatusUIComponentCustomSettings => {
    const settings = obj as EntitySummaryWithStatusUIComponentCustomSettings;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.titleKey)) {
        return errorLogger?.logError({
            message: '"titleKey" must be undefined or a non-empty string.',
            value  : settings.titleKey,
            path   : [...configPath, 'titleKey'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.icon)) {
        return errorLogger?.logError({
            message: '"icon" must be undefined or a non-empty string.',
            value  : settings.icon,
            path   : [...configPath, 'icon'],
        }) ?? false;
    }

    if (!isValidSettingNumberValue(settings.decimalPlaces, false, false, 0, 10)) {
        return errorLogger?.logError({
            message: '"decimalPlaces" must be undefined or an integer between 0 and 10 inclusive',
            value  : settings.decimalPlaces,
            path   : [...configPath, 'decimalPlaces'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.defaultValueKey, false, true)) {
        return errorLogger?.logError({
            message: '"defaultValueKey" must be undefined or a string.',
            value  : settings.defaultValueKey,
            path   : [...configPath, 'defaultValueKey'],
        }) ?? false;
    }

    if (!isValidSettingBooleanValue(settings.showEntityName, false)) {
        return errorLogger?.logError({
            message: '"showEntityName" must be undefined or a boolean',
            value  : settings.showEntityName,
            path   : [...configPath, 'showEntityName'],
        }) ?? false;
    }

    if (!isValidSettingBooleanValue(settings.showEntityImage, false)) {
        return errorLogger?.logError({
            message: '"showEntityImage" must be undefined or a boolean',
            value  : settings.showEntityImage,
            path   : [...configPath, 'showEntityImage'],
        }) ?? false;
    }

    // Validate the "displayData" array
    if (!(Array.isArray(settings.displayData)) || settings.displayData.length === 0) {
        return errorLogger?.logError({
            message: '"displayData" is required and must be a non-empty array.',
            value  : settings.displayData,
            path   : [...configPath, 'displayData'],
        }) ?? false;
    }

    // Make sure every element in the "displayData" are valid
    if (!settings.displayData.every((dataSettings, index) => {
        // Return "true" if dataSettings is one of the following type of EntityDataSettings.
        if (!isSubTypeEntityDataSettings(dataSettings, errorLogger, [...configPath, 'displayData', `[${index}]`])) {
            return false;
        }

        return true;
    })) {
        return false;
    }

    if (
        settings.highlightCardStyle !== undefined
        && !isCardHighlightStyle(settings.highlightCardStyle, errorLogger, [...configPath, 'highlightCardStyle'])
    ) {
        return false;
    }

    if (settings.styles) {
        if (!isObjectLiteral(settings.styles)) {
            return errorLogger?.logError({
                message: '"styles" must be undefined or an object.',
                value  : settings.styles,
                path   : [...configPath, 'styles'],
            }) ?? false;
        }

        // Make sure all the styles in the styles object are valid CSS Props objects
        if (!Object.entries(settings.styles).every(([name, style]) => {
            if (style !== undefined && !isObjectLiteral(style)) {
                return errorLogger?.logError({
                    message: `"${name}" must be undefined or a CSSProperties object.`,
                    value  : style,
                    path   : [...configPath, 'styles', `${name}`],
                }) ?? false;
            }
            return true;
        })) {
            return false;
        }
    }

    return true;
};



export interface EntitySummaryWithStatusUIComponentProps extends EntityUIComponentProps {
    readonly customSettings?: EntitySummaryWithStatusUIComponentCustomSettings | undefined;
}


/**
 * This EntityUIComponent is used for showing an entity's children's summary e.g. name, attribute values, tsdb values, etc...
 * in Tabular format.
 */
export const EntitySummaryWithStatusUIComponent: React.FC<EntitySummaryWithStatusUIComponentProps> = (
    props: EntitySummaryWithStatusUIComponentProps,
) => {

    const project = useSelectProject(props.projectId);
    const { trans } = useRitzTranslation(project);
    const selectedEntity = useSelectEntity(props.projectId, props.entityId);
    const entityClasses = useSelectTranslatedEntityClasses(props.projectId, trans);
    const classes = useStyle();
    const panelClasses = useModePanelStyle();


    // Because we need to show the latest value for all the data, we will use the current time as the date which we need to fetch data for.
    const date = useMemo(() => {
        return Date.now();
    }, []);


    // Create and cache the array of entities we will pass to the "useGetEntitiesData" hook to fetch data for. Even though there is only 1
    // entity, we still need to cache this array instead of creating a new one dynamically every time we call "useGetEntitiesData". This
    // will prevent the "useGetEntitiesData" form making unnecessary API calls
    const entities = useMemo(() => {
        if (selectedEntity) {
            return [selectedEntity];
        }
        return undefined;
    }, [selectedEntity]);


    // First, call the "useGetEntitiesData" to get the values for all the data we need to display
    const multipleEntitiesData = useGetEntitiesData(
        props.projectId,
        entities,
        entityClasses,
        props.customSettings?.displayData,
        date,
    );

    // The "useGetEntitiesData" fetch data for multiple entities and return the result as a map of entityId => EntityData[].
    // We need to lookup the for the selectedEntity.
    const multipleEntityData = useMemo(() => {
        if (multipleEntitiesData && selectedEntity) {
            return multipleEntitiesData[selectedEntity.entityId];
        }
        return undefined;
    }, [multipleEntitiesData, selectedEntity]);


    // The state to keep track of the entity's alerts summary, the # of HIGH alerts, the # of MODERATE alerts, and # of LOW alerts
    const [alertsSummary, setAlertsSummary] = useState<AlertsSummary>();

    // This useEffect is used for fetching the entity's alert summary on component loads
    useEffect(() => {
        getEntityAlertsSummary(props.projectId, props.entityId).then((response) => {
            setAlertsSummary(response);
        });
    }, [props.entityId, props.projectId]);

    // Once we have the entity's alerts summary, get the entity's  MOST severe alert
    const entityMostSevereAlert = useMemo((): AlertSeverity | undefined => {
        if (alertsSummary) {
            const highestSeverity = [AlertSeverity.HIGH, AlertSeverity.MODERATE, AlertSeverity.LOW].find((severity) => {
                return alertsSummary[severity] > 0;
            });
            return highestSeverity;
        }
        return undefined;
    }, [alertsSummary]);


    return (
        <>
            {selectedEntity && multipleEntityData ? (
                <Paper
                    className={clsx(
                        props.className, classes.root, panelClasses.root, props.onClick && 'clickable',
                        props.customSettings?.highlightCardStyle !== undefined && `highlight ${props.customSettings?.highlightCardStyle}`,
                        `severity-${entityMostSevereAlert}`,
                    )}
                    style={{
                        ...props.style,
                        ...props.customSettings?.styles?.container,
                    }}
                    elevation={2}
                    onClick={props.onClick}
                >
                    <InfoCompPanelHeader
                        title={props.customSettings?.titleKey ? trans(props.customSettings.titleKey) : undefined}
                        icon={props.customSettings?.icon}
                        headerless={!props.customSettings?.titleKey && !props.customSettings?.icon}
                    />
                    <div className={clsx(panelClasses.panelContent, classes.panelContent)}>
                        <div
                            className={clsx(classes.content)}
                            style={{
                                ...props.customSettings?.styles?.content,
                            }}
                        >
                            {props.customSettings?.showEntityName && (
                                <Typography
                                    className={classes.entityName}
                                    style={{
                                        ...props.customSettings?.styles?.name,
                                    }}
                                    variant="h6"
                                >{selectedEntity.displaySettings.name}
                                </Typography>
                            )}
                            <div className={classes.body}>
                                {props.customSettings?.showEntityImage && (
                                    <div
                                        className={classes.imageContainer}
                                        style={{
                                            ...props.customSettings?.styles?.imageContainer,
                                        }}
                                    >
                                        <img
                                            className={classes.image}
                                            style={{
                                                ...props.customSettings?.styles?.image,
                                            }}
                                            src={selectedEntity.displaySettings.imageData?.url ?? BLANK_IMAGE}
                                            alt={selectedEntity.displaySettings.imageData?.url ?? selectedEntity.displaySettings.name}
                                        />
                                    </div>
                                )}
                                <div
                                    className={classes.dataDisplayContainer}
                                    style={{
                                        ...props.customSettings?.styles?.dataContainer,
                                    }}
                                >
                                    {multipleEntityData.map((data, colIndex) => {

                                        const value = data?.value;

                                        // If dataSettings has decimalPlaces setting then use it. Else use the global decimalPlaces settings
                                        const decimalPlaces = data && (
                                            isCustomAttributeEntityDataSettings(data.settings) || isTSDBEntityDataSettings(data.settings)
                                        ) && data.settings.decimalPlaces !== undefined
                                            ? data.settings.decimalPlaces
                                            : props.customSettings?.decimalPlaces;

                                        const defaultValueKey = data?.settings.defaultValueKey ?? props.customSettings?.defaultValueKey;
                                        const defaultValue = defaultValueKey !== undefined ? trans(defaultValueKey) : NO_VALUE;

                                        return (
                                            <div
                                                key={`${data.settings.source}-${data.settings.name}-${colIndex}`}
                                                className={clsx(classes.entityData)}
                                                style={{
                                                    ...props.customSettings?.styles?.dataRow,
                                                }}
                                            >
                                                <div
                                                    className={classes.entityDataLabel}
                                                    style={{
                                                        ...props.customSettings?.styles?.dataLabel,
                                                    }}
                                                >{data.label}
                                                </div>
                                                <div
                                                    className={classes.entityDataValueAndUnit}
                                                    style={{
                                                        ...props.customSettings?.styles?.dataValue,
                                                    }}
                                                >
                                                    {value !== undefined && typeof value === 'number' && decimalPlaces !== undefined ? (
                                                        <span className="value">
                                                            {trans('{{value, number-format}}', {
                                                                value,
                                                                decimalPlaces,
                                                            })}
                                                        </span>
                                                    ) : (
                                                        <span className="value">{value ?? defaultValue}</span>
                                                    )}
                                                    {data.unit !== undefined && (
                                                        <span className="unit">{data.unit}</span>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                </Paper>
            ) : (
                <CircularProgress />
            )}
        </>
    );
};


// Register this component with the RitzComponentsCollection so that it can be used with the Ritz UI customization system
RitzComponentsCollection.getInstance().registerComponent(
    'EntitySummaryWithStatusUIComponent', EntitySummaryWithStatusUIComponent,
);



// Register the component with the EntityUIComponentCollection so that the component can be used by the Entity Layout System
EntityUIComponentCollection.getInstance().registerComponent(
    'Entity_Summary_With_Status_Comp',
    {
        componentFunc          : EntitySummaryWithStatusUIComponent,
        customSettingsValidator: isEntitySummaryWithStatusUIComponentCustomSettings,
    },
);
